import React, { useEffect, useState, useRef, useCallback } from 'react'
import { Container, Row, Col } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from "react-i18next"
import { getClubsPublic } from '../../actions/profiles/clubsActions'
import { getActivitiesPublic } from '../../actions/activities/activitiesActions'
import { getCourtsPublicByFilter, getMatchesPublicByFilter } from '../../actions/filtersActions'
import MenuMarketplace from '../../components/menus/MenuMarketplace'
import ActivityMarket from '../../components/Activities/ActivityMarket'
import CourtMarket from '../../components/Renting/CourtMarket'
import MatchMarket from '../../components/ticketing/MatchMarket'
import { FooterMarketplace } from '../../components/footers/FooterMarketplace'
import BannerFullWidth from '../../components/Banners/BannerFullWidth'
import { Link } from "react-router-dom"

// Estilos de cada posición del coverflow: -3 (saliendo) … 0 (centro) … +3 (entrando)
const COVERFLOW_STYLES = {
    '-3': { transform: 'translateX(-750px) scale(0.28)', opacity: 0,    zIndex: 0, pointerEvents: 'none' },
    '-2': { transform: 'translateX(-500px) scale(0.48)', opacity: 0.35, zIndex: 1 },
    '-1': { transform: 'translateX(-250px) scale(0.70)', opacity: 0.65, zIndex: 2 },
     '0': { transform: 'translateX(0px)    scale(1.3)',  opacity: 1,    zIndex: 5 },
     '1': { transform: 'translateX(250px)  scale(0.70)', opacity: 0.65, zIndex: 2 },
     '2': { transform: 'translateX(500px)  scale(0.48)', opacity: 0.35, zIndex: 1 },
     '3': { transform: 'translateX(750px)  scale(0.28)', opacity: 0,    zIndex: 0, pointerEvents: 'none' },
}

const shuffleArray = (items = []) => {
    const shuffled = [...items]

    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        const temp = shuffled[i]
        shuffled[i] = shuffled[j]
        shuffled[j] = temp
    }

    return shuffled
}

const getHighlightsSignature = (items = []) => items
    .map(({ type, item }) => `${type}:${item?._id || item?.url || ''}`)
    .join('|')

const buildHomeHighlights = ({ activities = [], courts = [], matches = [], limit = 8 }) => {
    const mixedItems = [
        ...activities.map(item => ({ type: 'activity', item })),
        ...courts.map(item => ({ type: 'court', item })),
        ...matches.map(item => ({ type: 'match', item })),
    ]

    return shuffleArray(mixedItems).slice(0, limit)
}

const buildUniqueHomeHighlights = ({ activities = [], courts = [], matches = [], limit = 8 }) => {
    const storageKey = 'incrono-home-last-marketplace-feed'
    const previousSignature = localStorage.getItem(storageKey)
    let nextHighlights = buildHomeHighlights({ activities, courts, matches, limit })
    let attempts = 0

    while (
        nextHighlights.length > 1 &&
        getHighlightsSignature(nextHighlights) === previousSignature &&
        attempts < 6
    ) {
        nextHighlights = buildHomeHighlights({ activities, courts, matches, limit })
        attempts += 1
    }

    localStorage.setItem(storageKey, getHighlightsSignature(nextHighlights))

    return nextHighlights
}

export default function Home() {
    const { t } = useTranslation()	
    const dispatch = useDispatch()

    const {activitiesMarket: activities} = useSelector(state=>state.activities)
    const {clubsMarket: clubs} = useSelector(state=>state.clubs)
    const {courtsfilteredMarket: courts, matchesFilteredMarket: matches} = useSelector(state=>state.filters)

    const [activeClubs, setActiveClubs] = useState([])
    const [marketplaceHighlights, setMarketplaceHighlights] = useState([])
    const [coverflowItems, setCoverflowItems] = useState([])
    const nextClubIdxRef = useRef(0)

    const [showVideo, setShowVideo] = useState(true)

    useEffect(() =>{
        window.scrollTo({
            top: 0,
            behavior: 'instant',
        })
        dispatch(getClubsPublic())
        dispatch(getActivitiesPublic())
        dispatch(getCourtsPublicByFilter({ club: null, site: null, court: null, sport: null }))
        dispatch(getMatchesPublicByFilter({ host: '', visitor: '', date: '', club: null }))
    }, [dispatch])

    useEffect(() =>{
        if(clubs){
            const filteredClubs = [...clubs].filter(club => club.visibleMarketplace !== false && club.active !== false)
            setActiveClubs(filteredClubs)
        }
    }, [clubs])

    // Inicializar coverflow con 5 items (-2 … +2) con índice aleatorio
    useEffect(() => {
        if (activeClubs.length === 0) return
        const n = activeClubs.length
        // Comenzar desde un índice aleatorio
        const randomStartIdx = Math.floor(Math.random() * n)
        const initial = [-2, -1, 0, 1, 2].map((pos, i) => ({
            uid: `init-${i}-${Date.now()}`,
            clubIdx: (randomStartIdx + pos + n) % n,
            pos,
        }))
        const rightmostClubIdx = (randomStartIdx + 2 + n) % n
        nextClubIdxRef.current = (rightmostClubIdx + 1) % n
        setCoverflowItems(initial)
    }, [activeClubs])

    const advanceCarousel = useCallback(() => {
        if (activeClubs.length === 0) return
        const n = activeClubs.length
        setCoverflowItems(prev => {
            // Eliminar los que ya salieron por la izquierda (-3)
            const kept = prev.filter(item => item.pos > -3)
            // Desplazar todos uno a la izquierda
            const shifted = kept.map(item => ({ ...item, pos: item.pos - 1 }))
            // Añadir nuevo item entrando por la derecha en posición +2
            const nextIdx = nextClubIdxRef.current
            shifted.push({
                uid: `${activeClubs[nextIdx]?._id || nextIdx}-${Date.now()}`,
                clubIdx: nextIdx,
                pos: 2,
            })
            nextClubIdxRef.current = (nextIdx + 1) % n
            return shifted
        })
    }, [activeClubs])

    // Autoplay cada 4 segundos
    useEffect(() => {
        if (activeClubs.length <= 1) return
        const timer = setInterval(advanceCarousel, 4000)
        return () => clearInterval(timer)
    }, [advanceCarousel])

    useEffect(() =>{
        const filteredActivities = Array.isArray(activities)
            ? activities.filter(activity =>
                activity?.active !== false &&
                activity?.visibility !== false
            )
            : []

        const filteredCourts = Array.isArray(courts)
            ? courts.filter(court =>
                court?.active !== false
            )
            : []

        const filteredMatches = Array.isArray(matches)
            ? matches.filter(match => match?.active !== false && match?.club?.active !== false)
            : []

        const nextHighlights = buildUniqueHomeHighlights({
            activities: filteredActivities,
            courts: filteredCourts,
            matches: filteredMatches,
            limit: 8,
        })

        setMarketplaceHighlights(nextHighlights)
    }, [activities, courts, matches])

    useEffect(() => {
        const handleResize = () => {
          // Verificar el tamaño de la pantalla para mostrar u ocultar el video
          if (window.innerWidth < 980) {
            setShowVideo(false) // Oculta el video en dispositivos móviles
          } else {
            setShowVideo(true) // Muestra el video en pantallas más grandes
          }
        };
    
        // Inicialmente, verificar el tamaño de la pantalla
        handleResize()
    
        // Agregar un event listener para manejar el redimensionamiento
        window.addEventListener('resize', handleResize)
    
        // Limpiar el event listener cuando el componente se desmonte
        return () => {
          window.removeEventListener('resize', handleResize)
        };
      }, [])

    return (
        <div>
            
            
            <div className='firstView d-flex align-items-center justify-content-center'>
                {showVideo && (
                    <div className="video-background">
                        <video autoPlay loop muted className='d-none d-sm-block'>
                            <source src="/videos/video-presentacion.mp4" type="video/mp4" />
                            Tu navegador no soporta la reproducción de video.
                        </video>
                    </div>
                )}
                <div className='position-relative d-flex flex-column align-items-center text-center superior-home'>
                    <figure className='mb-5'><img src='/images/logo-marketplace.svg' alt="Logotipo Incrono" style={{width: '450px'}} /></figure>
                    <h2 className='font-generic text-white'>{t('marketplace.home.phrase1')}</h2>
                    <h4 className='font-generic fw-light text-white'>{t('marketplace.home.phrase2')}</h4>
                    <div className='clubs-coverflow mt-5'>
                        <div className='clubs-coverflow-stage'>
                            {coverflowItems.map(item => {
                                const club = activeClubs[item.clubIdx]
                                if (!club) return null
                                const posStyle = COVERFLOW_STYLES[String(item.pos)] || { opacity: 0 }
                                return (
                                    <Link
                                        key={item.uid}
                                        to={`/${t("url.marketplace.clubs")}/${club?.url}`}
                                        className='clubs-coverflow-item'
                                        style={posStyle}
                                    >
                                        <img
                                            src={club?.avatar || '/images/avatar-club.png'}
                                            alt={club?.name || 'Club'}
                                        />
                                        {item.pos === 0 &&
                                            <span className='clubs-coverflow-name'>{club?.name}</span>
                                        }
                                    </Link>
                                )
                            })}
                        </div>
                    </div>
                    {/* <h4 className='font-generic text-white mt-5 mb-1'>{t('marketplace.home.phrase3')}</h4>
                    <h4 className='font-generic fw-light text-white'>{t('marketplace.home.phrase4')}</h4>
                    <Link to={`/${t("url.auth.registerclub")}`} className="btn btn-secondary mt-3">
                        {t('marketplace.home.discoverincrono')}
                    </Link> */}
                </div>
            </div>
            <MenuMarketplace className='m-0'/>
            <div className='marketplace mb-0'>
                <Container className='market-border'>
                    <Row>
                        <Col>
                            <h2 className='display-6 text-center'>{t('marketplace.lastactivities') }</h2>                            
                        </Col>
                    </Row>
                    <Row>
                        {marketplaceHighlights?.map((entry, index) => (
                            <Col key={`${entry?.type}-${entry?.item?._id || index}`} xs={12} sm={6} lg={3} className="my-3">
                                {entry?.type === 'activity' && <ActivityMarket activity={entry.item} />}
                                {entry?.type === 'court' && <CourtMarket court={entry.item} skipCommission hidePrice />}
                                {entry?.type === 'match' && <MatchMarket match={entry.item} skipCommission />}
                            </Col>
                        ))}
                    </Row>
                </Container>
            </div>
            <BannerFullWidth />
            {/* <div className='marketplace mb-0'>
                <Container className='market'>
                    <Row>
                        <Col xs='12 my-5 pt-5'>
                            <h2 className='display-6 text-center'>{t('marketplace.userexperience') }</h2>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Grid type="opinion"  />
                        </Col>
                    </Row>
                </Container>
            </div> */}
            <div className='marketplace mb-0 py-sm-4'>
            </div>
            {/* <div className='marketplace mb-0'>
                <Container className='market h-100'>
                    <Row className='h-100'>
                        <Col xs={12} lg={8} className='columna1 d-flex flex-column'>
                            <Suscribete />
                        </Col>
                        <Col xs={12} lg={4} className='columna2 d-flex flex-column'>
                            <Row className='flex-grow-0 mt-3 mt-sm-0'>
                                <Col>
                                    <UsersRegistered />
                                </Col>
                            </Row>
                            <Row className='mt-3 flex-grow-1 d-flex'>
                                <Col className='d-flex flex-column'>
                                    <BannerPhrase
                                        className='flex-grow-1'
                                        phrase={t('marketplace.suscribetitle')}
                                        image="/images/banner-incrono01.jpg"
                                    />
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </Container>
            </div> */}
            <FooterMarketplace className='m-0'/>
            
        </div>
    )
}